import { memo, useCallback } from 'react';
import {
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
} from 'reactflow';

import { useUIContext } from '../../../providers/UIProvider';
import useStore from '../../../store/useStore';
import { FlowState } from '../../../store/useStore';

interface CustomEdgeData {
  label?: string;
  status?: 'active' | 'inactive' | 'error';
}

/**
 * Enhanced edge component that supports:
 * - Selection highlighting with primary-500 color
 * - Status-based styling (active/inactive/error)
 * - Edge labels with styling
 * - Dark mode support
 * - Wider click target for better UX
 */
const CustomEdge = memo(({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  selected,
}: EdgeProps<CustomEdgeData>) => {
  const { isDarkMode } = useUIContext();
  const selectedEdge = useStore((state: FlowState) => state.selectedEdge);
  const setSelectedEdge = useStore((state: FlowState) => state.setSelectedEdge);
  
  // Use the ReactFlow selection state or our custom selection state
  const isSelected = selected || selectedEdge === id;

  // Get the path for the edge
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleClick = useCallback((event: React.MouseEvent) => {
    // Stop propagation to prevent the canvas onClick from being triggered
    event.stopPropagation();
    
    // Get the selected node from the store
    const selectedNode = useStore.getState().selectedNode;
    const setSelectedNode = useStore.getState().setSelectedNode;
    
    // Check if shift key is pressed for multi-selection
    // const isShiftPressed = event.shiftKey;
    
    // If this edge is already selected, keep it selected and just handle node deselection
    if (selectedEdge === id) {
      // If there's a selected node, deselect it
      if (selectedNode !== null) {
        setSelectedNode(null);
        
        // Also deselect all nodes in ReactFlow
        const currentNodes = useStore.getState().nodes;
        const updatedNodes = currentNodes.map(n => ({
          ...n,
          selected: false
        }));
        useStore.getState().setNodes(updatedNodes);
      }
      // Keep the edge selected - don't toggle
      return;
    }
    
    // Deselect any selected node when clicking on an edge
    if (selectedNode !== null) {
      setSelectedNode(null);
      
      // Also deselect all nodes in ReactFlow
      const currentNodes = useStore.getState().nodes;
      const updatedNodes = currentNodes.map(n => ({
        ...n,
        selected: false
      }));
      useStore.getState().setNodes(updatedNodes);
    }
    
    // Always select this edge (no toggling when coming from a node selection)
    setSelectedEdge(id);
  }, [id, setSelectedEdge, selectedEdge]);

  const getEdgeColor = () => {
    if (isSelected) return '#3b82f6'; // Tailwind primary-500 color
    if (data?.status === 'error') return '#ef4444'; // Semantic red for errors
    if (data?.status === 'inactive') return isDarkMode ? '#94a3b8' : '#64748b';
    return isDarkMode ? '#94a3b8' : '#475569';
  };

  return (
    <g>
      <BaseEdge
        path={edgePath}
        style={{
          strokeWidth: isSelected ? 3 : 2, // 1px thicker when selected as requested
          stroke: getEdgeColor(),
          transition: 'stroke-width 0.2s, stroke 0.2s',
          zIndex: 50, // Add zIndex to all edges
          ...style,
        }}
        markerEnd={markerEnd}
      />
      
      {/* Invisible wider path for better click handling */}
      <path
        id={id}
        style={{ strokeWidth: 20, opacity: 0, cursor: 'pointer', zIndex: 50 }}
        d={edgePath}
        onClick={handleClick}
      />
      
      {data?.label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              whiteSpace: 'nowrap',
            }}
            className={`
              px-2 py-1 text-xs font-medium rounded-md
              ${isSelected 
                ? isDarkMode ? 'bg-blue-900 text-blue-200 border-blue-800' : 'bg-blue-50 text-blue-700 border-blue-200'
                : isDarkMode ? 'bg-gray-800 text-gray-200 border-gray-700' : 'bg-white text-gray-600 border-gray-200'
              }
              shadow-sm border transition-colors
              hover:bg-gray-50 dark:hover:bg-gray-700
            `}
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      )}
    </g>
  );
});

CustomEdge.displayName = 'CustomEdge';

export { CustomEdge };
